import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  type AnyBlock,
  type ARIAFilterSuggestion,
  caseInsensitiveStringCompare,
  escapeString,
  type FilterConfig,
  type FilterKey,
  type FilterProvider,
  FilterProviderType,
  type FilterQuery,
  type FilterValueData,
  FilterValueType,
  getDefaultValueVisualDescriptors,
  getFilterValue,
  getHasValueSuggestion,
  getLastFilterBlockValue,
  getNoValueSuggestion,
  getUnescapedFilterValue,
  type IndexedBlockValueItem,
  isEmptyFilterBlockValue,
  type MutableFilterBlock,
  NOT_SHOWN,
  type SuppliedFilterProviderOptions,
  type UnrankedARIAFilterSuggestion,
  type ValueRowProps,
} from '@github-ui/filter-core'
import {AsyncFilterProvider} from '@github-ui/filter-core/providers'
import {fuzzyScore} from '@github-ui/fuzzy-score/fuzzy-score'
import type {FilterSuggestionsProjectIssueTypesQuery} from '@github-ui/server-persisted-queries/types/FilterSuggestionsProjectIssueTypes'
import type {FilterSuggestionsRepoIssueTypesQuery} from '@github-ui/server-persisted-queries/types/FilterSuggestionsRepoIssueTypes'
import type {FilterSuggestionsViewerIssueTypesQuery} from '@github-ui/server-persisted-queries/types/FilterSuggestionsViewerIssueTypes'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {GitPullRequestIcon, IssueOpenedIcon} from '@primer/octicons-react'
import {hasMatch} from 'fzy.js'

const LEGACY_TYPE_VALUES = [
  {value: 'issue', displayName: 'Issue', priority: 1, icon: IssueOpenedIcon},
  {value: 'pr', displayName: 'Pull Request', priority: 2, icon: GitPullRequestIcon},
]

type IssueType = {
  name: string
}

type RawResponseData =
  | FilterSuggestionsRepoIssueTypesQuery
  | FilterSuggestionsProjectIssueTypesQuery
  | FilterSuggestionsViewerIssueTypesQuery

function extractNamesFromRawResponse(data: RawResponseData): string[] {
  if ('repository' in data && data.repository) {
    const edges = data.repository.issueTypes?.edges ?? []
    return edges.flatMap(e => (e?.node ? [e.node.name] : []))
  }

  if ('viewer' in data && data.viewer) {
    return [...(data.viewer.suggestedIssueTypeNames ?? [])]
  }

  if ('organization' in data || 'user' in data) {
    const projectData = data as FilterSuggestionsProjectIssueTypesQuery
    const names =
      projectData.organization?.projectV2?.suggestedIssueTypeNames ??
      projectData.user?.projectV2?.suggestedIssueTypeNames
    return [...(names ?? [])]
  }

  return []
}

export type ProjectScope = {
  login?: string
  projectNumber?: number
}

export const ISSUE_TYPE_FILTER_KEY: FilterKey<'type'> = {
  displayName: 'Type',
  key: 'type',
  priority: NOT_SHOWN,
  icon: IssueOpenedIcon,
  description: 'Specify the type of issue to search for',
}

export class IssueTypeFilterProvider extends AsyncFilterProvider<IssueType> implements FilterProvider {
  filterValues?: ARIAFilterSuggestion[]
  repositoryScope?: string
  projectScope?: ProjectScope
  owner?: string
  repo?: string
  legacy: boolean
  #cachedNames: string[] | null = null

  constructor(
    options?: SuppliedFilterProviderOptions,
    legacy: boolean = false,
    repositoryScope?: string,
    projectScope?: ProjectScope,
  ) {
    super(ISSUE_TYPE_FILTER_KEY, {...options, filterTypes: {...options?.filterTypes, multiValue: true}})
    this.type = FilterProviderType.Text
    this.repositoryScope = repositoryScope
    this.projectScope = projectScope
    this.legacy = legacy
  }

  async getSuggestions(
    filterQuery: FilterQuery,
    filterBlock: AnyBlock | MutableFilterBlock,
    config: FilterConfig,
    caretIndex?: number | null,
  ) {
    const lastValue = getLastFilterBlockValue(filterBlock, caretIndex)
    const suggestions =
      (await this.processSuggestions(filterQuery, filterBlock, this.#processSuggestion.bind(this), caretIndex)) || []

    const rankedSuggestions = []
    if (
      isEmptyFilterBlockValue(filterBlock) &&
      this.options.filterTypes.valueless &&
      !filterBlock.raw.startsWith('-')
    ) {
      const noValueSuggestion = getNoValueSuggestion(this.displayName, this.icon)
      rankedSuggestions.push(noValueSuggestion)
    }

    if (isEmptyFilterBlockValue(filterBlock) && this.options.filterTypes.hasValue && !filterBlock.raw.startsWith('-')) {
      const hasValueSuggestion = getHasValueSuggestion(this.displayName, this.icon)
      rankedSuggestions.push(hasValueSuggestion)
    }

    for (const suggestion of suggestions) {
      const {value: fValue, displayName} = suggestion
      const value = getFilterValue(fValue)

      const matchesValue = !!value && hasMatch(lastValue, value)
      const matchesDisplayName = !!displayName && hasMatch(lastValue, displayName)

      if (!value || (!matchesValue && !matchesDisplayName)) continue

      suggestion.priority -= fuzzyScore(lastValue, value)

      rankedSuggestions.push(suggestion)
    }

    return rankedSuggestions
  }

  override async fetchSuggestions(value: string, block: AnyBlock | MutableFilterBlock): Promise<IssueType[] | null> {
    if (!block) return []

    try {
      const names = await this.#fetchIssueTypeNamesRest()
      return names.map(name => ({name}))
    } catch {
      return []
    }
  }

  override async validateFilterValue(value: string): Promise<IssueType | null> {
    try {
      if (this.projectScope) {
        return {name: value} as IssueType
      }
      const names = await this.#fetchIssueTypeNamesRest()
      // If the endpoint is unavailable (e.g. flag not rolled out on this stamp),
      // accept the value rather than marking it invalid
      if (names.length === 0) {
        return {name: value} as IssueType
      }
      const matched = names.find(name => caseInsensitiveStringCompare(name, value))
      return matched ? ({name: matched} as IssueType) : null
    } catch {
      return {name: value} as IssueType
    }
  }

  override validateValue(
    filterQuery: FilterQuery,
    value: IndexedBlockValueItem,
    issueType: IssueType | null,
  ): false | Partial<IndexedBlockValueItem> | null | undefined {
    const extractedValue = getUnescapedFilterValue(value.value)
    const validValues = this.legacy
      ? [...LEGACY_TYPE_VALUES.map(({value: typeValue}) => typeValue), issueType?.name]
      : [issueType?.name]
    if (validValues.length === 0 || !extractedValue) return false

    const matchedName = validValues.find(name => caseInsensitiveStringCompare(name, extractedValue))
    if (!matchedName) return false

    return {
      value: escapeString(extractedValue) ?? '',
      displayName: extractedValue,
    }
  }

  override async processSuggestions(
    filterQuery: FilterQuery,
    filterBlock: AnyBlock | MutableFilterBlock,
    processSuggestion: (item: IssueType, query: string, filterQuery: FilterQuery) => ARIAFilterSuggestion,
    caretIndex?: number | null,
  ) {
    if (!this.shouldGetSuggestions(filterBlock)) return null

    const lastValue = getLastFilterBlockValue(filterBlock, caretIndex)
    const items = (await this.fetchSuggestions(lastValue, filterBlock)) ?? []
    const suggestions: ARIAFilterSuggestion[] = this.legacy
      ? LEGACY_TYPE_VALUES.map(fv => ({...fv, ariaLabel: `${fv.displayName}, ${this.displayName}`}))
      : []

    return [...suggestions, ...items.map(item => processSuggestion(item, lastValue, filterQuery))]
  }

  getValueRowProps(value: FilterValueData): ValueRowProps {
    return {
      text: value.displayName ?? getFilterValue(value.value) ?? '',
      leadingVisual: getDefaultValueVisualDescriptors(value, this.icon),
    }
  }

  override rankSuggestion(query: string, suggestion: UnrankedARIAFilterSuggestion): ARIAFilterSuggestion {
    const {displayName: name} = suggestion
    let priority = 3

    if (query) {
      if (name) priority -= fuzzyScore(query, name)
    }

    return {...suggestion, priority}
  }

  #processSuggestion(issueType: IssueType, query: string): ARIAFilterSuggestion {
    const {name} = issueType

    return this.rankSuggestion(query, {
      type: FilterValueType.Value,
      displayName: name,
      ariaLabel: `${name}, ${this.displayName}`,
      value: escapeString(name) ?? '',
      inlineDescription: true,
      icon: this.icon,
    })
  }

  async #fetchIssueTypeNamesRest(): Promise<string[]> {
    if (this.#cachedNames) return this.#cachedNames

    const url = this.#buildEndpointUrl()
    const response = await verifiedFetchJSON(url)

    if (!response.ok) return []

    const json = await response.json()

    if (isFeatureEnabled('issue_type_filter_raw_response')) {
      const rawResponse = json as {data?: RawResponseData}
      if (!rawResponse.data) return []
      this.#cachedNames = extractNamesFromRawResponse(rawResponse.data)
    } else {
      const data = json as {names: string[]}
      this.#cachedNames = data.names ?? []
    }

    return this.#cachedNames
  }

  #buildEndpointUrl(): string {
    const base = '/_ghui/filter-suggestions/issue_types'

    if (this.repositoryScope) {
      const [owner, repo] = this.repositoryScope.split('/')
      if (!owner || !repo) return base
      const params = new URLSearchParams({owner, repo})
      return `${base}?${params.toString()}`
    } else if (this.projectScope?.login && this.projectScope?.projectNumber) {
      const params = new URLSearchParams()
      params.set('login', this.projectScope.login)
      params.set('project_number', String(this.projectScope.projectNumber))
      return `${base}?${params.toString()}`
    }

    return base
  }
}
